import { useCallback } from 'react'
import lodashSet from 'lodash/set'
import lodashMerge from 'lodash/merge'
import * as yup from 'yup'

/**
 * Accept an optional `customValidateFn` when one want to provide some rather complex validation rule
 * that sit outside the Yup Schema. (ie. validate against some computed value)
 *
 * The function should return an error object, which then be merged with the `YupValidationResult`
 */
export function useYupValidationResolver<T>(
  validationSchema: yup.AnySchema,
  customValidateFn?: (data: T) => any // eslint-disable-line @typescript-eslint/no-explicit-any
) {
  return useCallback(
    async (data) => {
      let result = { values: data, errors: {} }

      try {
        await validationSchema.validate(data, {
          abortEarly: false,
        })
      } catch (ex) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errors: any = {}

        ;(ex.inner || []).forEach(
          (currentError: { path: string; type: string; message: string }) => {
            lodashSet(errors, currentError.path, {
              type: currentError.type ?? 'validation',
              message: currentError.message,
            })
          }
        )
        result = {
          values: data,
          errors,
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const customValidationErrors = customValidateFn ? customValidateFn(data as any) : null

      const finalErrors = lodashMerge(result.errors, customValidationErrors)

      return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        values: Object.keys(finalErrors).length > 0 ? ({} as any) : data,
        errors: finalErrors,
      }
    },
    [validationSchema, customValidateFn]
  )
}
